import { useCallback, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import {
  closePrivateChatPresenceConnection,
  establishPrivateChatPresenceConnection,
  removePrivateChatPresenceListener,
  PrivateChatPresencePayload,
} from "../services/privateChatPresenceSignalRService";
import CryptoManager from "../utils/cryptoManager";
import {
  createPrivateChat,
  createPrivateChatByPublicId,
  markChatSeen,
  getPrivateChatInfo,
} from "../api/chatApi";
import {
  loadOtherSession,
  saveOtherSession,
  expireSessionNow,
} from "../utils/sessionManager";
import { FetchedPrivateMessage, SeenPayload } from "../api/types";
import { useNavigate } from "react-router-dom";
import { PublicIdCode } from "../utils/publicIdCode";
import { getUserFacingErrorMessage } from "./errorMessages";
import { DEFAULT_TIMER_SECONDS } from "../constants/chat";

const PENDING_MOBILE_TRANSFER_KEY = "idlessPendingMobileTransfer";

type UseChatConnectionParams = {
  mode: "chatNumber" | "publicId";
  identifier?: string;
  chatNumber?: string;
  publicId?: string;
  myUserId?: string;
  requestToken?: string;
  isActive: boolean;
  onReceiveMessage: (plainText: string) => void;
  onReceiveFetchedMessage: (message: {
    id: string;
    plainText: string;
    sender: "sent" | "received";
    dateCreated?: string;
    expiresAt?: number;
    status?: "sent" | "delivered" | "failed";
  }) => void;
  onReceiveDecryptionFailure: () => void;
  onSeen: () => void;
  onShortExpiryUpdated?: (ms: number) => void;
  onPublicIdReplaced?: (publicId: string) => void;
};

type UseChatConnectionReturn = {
  connection: signalR.HubConnection | null;
  isChatReady: boolean;
  otherUserName: string;
  chatConnection: string;
  refreshMissedMessages: () => Promise<void>;
};

function getErrorMessage(error: unknown): string {
  return getUserFacingErrorMessage(error, "Could not initialize chat");
}

function getSignalRIdentifierFromPublicId(value: string): string {
  const decoded = PublicIdCode.decode(value);

  if (decoded !== null && decoded !== undefined) {
    return String(decoded);
  }

  return value;
}

export function useChatConnection({
  mode,
  identifier,
  chatNumber,
  publicId,
  myUserId,
  requestToken,
  isActive,
  onReceiveMessage,
  onReceiveDecryptionFailure,
  onReceiveFetchedMessage,
  onSeen,
  onShortExpiryUpdated,
  onPublicIdReplaced,
}: UseChatConnectionParams): UseChatConnectionReturn {
  const navigate = useNavigate();

  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null,
  );
  const [isChatReady, setIsChatReady] = useState(false);
  const [otherUserName, setOtherUserName] = useState("");
  const [chatConnection, setChatConnection] = useState("");

  const onReceiveMessageRef = useRef(onReceiveMessage);
  const onReceiveDecryptionFailureRef = useRef(onReceiveDecryptionFailure);
  const onSeenRef = useRef(onSeen);
  const onShortExpiryUpdatedRef = useRef(onShortExpiryUpdated);
  const isActiveRef = useRef(isActive);

  const creatingChatRef = useRef<Promise<any> | null>(null);

  const onReceiveFetchedMessageRef = useRef(onReceiveFetchedMessage);
  const fetchedMessageIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    onReceiveFetchedMessageRef.current = onReceiveFetchedMessage;
  }, [onReceiveFetchedMessage]);

  useEffect(() => {
    onReceiveMessageRef.current = onReceiveMessage;
  }, [onReceiveMessage]);

  useEffect(() => {
    onReceiveDecryptionFailureRef.current = onReceiveDecryptionFailure;
  }, [onReceiveDecryptionFailure]);

  useEffect(() => {
    onSeenRef.current = onSeen;
  }, [onSeen]);

  useEffect(() => {
    onShortExpiryUpdatedRef.current = onShortExpiryUpdated;
  }, [onShortExpiryUpdated]);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const applyTimerFromChatInfo = useCallback((chat: any) => {
    if (!chat) return;

    if (typeof chat.minTimer === "number") {
      onShortExpiryUpdatedRef.current?.(chat.minTimer * 1000);
      return;
    }

    const myTimer =
      typeof chat.myTimer === "number" ? chat.myTimer : DEFAULT_TIMER_SECONDS;
    const otherTimer =
      typeof chat.otherTimer === "number"
        ? chat.otherTimer
        : DEFAULT_TIMER_SECONDS;

    onShortExpiryUpdatedRef.current?.(Math.min(myTimer, otherTimer) * 1000);
  }, []);

  const applyUpdatedChat = useCallback(
    (payload: any) => {
      let chat = payload?.chat;
      if (!chat) return;

      if (typeof chat === "string") {
        try {
          const parsed = JSON.parse(chat);
          chat = parsed?.chat ?? parsed;
        } catch (error) {
          console.error("Failed to parse updatedChat chat string:", error);
          return;
        }
      } else if (typeof chat === "object" && chat?.chat) {
        chat = chat.chat;
      }

      applyTimerFromChatInfo(chat);
    },
    [applyTimerFromChatInfo],
  );

  function isChatNotFoundError(error: unknown) {
    if (!error) return false;

    const anyError = error as any;

    return (
      anyError?.error === "Chat not found" ||
      anyError?.message === "Chat not found" ||
      anyError?.response?.error === "Chat not found" ||
      anyError?.data?.error === "Chat not found" ||
      JSON.stringify(error).includes("Chat not found")
    );
  }

  useEffect(() => {
    if (!requestToken) return;

    let settlingTimeout: number | null = null;
    let resolving = false;

    const isChatGoneFromServer = async () => {
      const otherSession = loadOtherSession();
      if (!otherSession?.otherUserId) return false;

      try {
        await getPrivateChatInfo(
          otherSession.otherUserId,
          requestToken,
          sessionStorage.getItem("additionalId") || undefined,
        );
        return false;
      } catch (error) {
        return isChatNotFoundError(error);
      }
    };

    const resolvePendingMobileTransfer = () => {
      if (document.visibilityState !== "visible" || resolving) return;

      const raw = sessionStorage.getItem(PENDING_MOBILE_TRANSFER_KEY);
      if (!raw) return;

      let pendingChatId: string | null = null;

      try {
        const parsed = JSON.parse(raw);
        pendingChatId =
          typeof parsed?.chatId === "string" ? parsed.chatId : null;
      } catch {
        pendingChatId = null;
      }

      const currentChatId = loadOtherSession()?.chatId ?? null;

      if (pendingChatId && currentChatId && pendingChatId !== currentChatId) {
        sessionStorage.removeItem(PENDING_MOBILE_TRANSFER_KEY);
        return;
      }

      resolving = true;

      settlingTimeout = window.setTimeout(async () => {
        sessionStorage.removeItem(PENDING_MOBILE_TRANSFER_KEY);

        // The page going hidden is only a hint (store redirect, iOS error
        // alert, app switch). The app deletes this chat when it takes it over,
        // so only treat it as transferred once the backend confirms that.
        if (!(await isChatGoneFromServer())) {
          resolving = false;
          return;
        }

        await closePrivateChatPresenceConnection().catch(() => {});
        await expireSessionNow();
        navigate("/transferred-chat", { replace: true });
      }, 300);
    };

    document.addEventListener(
      "visibilitychange",
      resolvePendingMobileTransfer,
    );
    window.addEventListener("focus", resolvePendingMobileTransfer);
    window.addEventListener("pageshow", resolvePendingMobileTransfer);

    resolvePendingMobileTransfer();

    return () => {
      document.removeEventListener(
        "visibilitychange",
        resolvePendingMobileTransfer,
      );
      window.removeEventListener("focus", resolvePendingMobileTransfer);
      window.removeEventListener("pageshow", resolvePendingMobileTransfer);

      if (settlingTimeout !== null) {
        window.clearTimeout(settlingTimeout);
      }
    };
  }, [navigate, requestToken]);

  const refreshMissedMessages = useCallback(async () => {
    if (!requestToken) return;

    const otherSession = loadOtherSession();
    if (!otherSession?.otherUserId) return;

    const additionalId = sessionStorage.getItem("additionalId");

    try {
      const freshChatInfo = await getPrivateChatInfo(
        otherSession.otherUserId,
        requestToken,
        additionalId || undefined,
      );

      applyTimerFromChatInfo(freshChatInfo);

      // resolvedDisplayName is the server-computed, authoritative display name
      // (falls through to the raw name for backwards compatibility with older
      // response shapes) -- the raw user.name may be omitted entirely when the
      // counterparty is shown behind a Context Tag and hasn't disclosed it.
      const resolvedName =
        freshChatInfo?.resolvedDisplayName ?? freshChatInfo?.user?.name;
      if (typeof resolvedName === "string") {
        setOtherUserName(resolvedName);
      }

      const fetchedMessages = freshChatInfo?.messages;

      if (!Array.isArray(fetchedMessages)) return;

      const myPrivateKey = await CryptoManager.getStoredPrivateKeyBase64();
      const senderPublicKey =
        freshChatInfo?.user?.publicKey ||
        otherSession.otherUserPubKey ||
        sessionStorage.getItem("otherUserPubKey");

      if (!myPrivateKey || !senderPublicKey) {
        onReceiveDecryptionFailureRef.current?.();
        return;
      }

      for (const message of fetchedMessages as FetchedPrivateMessage[]) {
        if (!message?.id || !message?.content) continue;

        if (fetchedMessageIdsRef.current.has(message.id)) {
          continue;
        }

        if (message.dateExpire && message.dateExpire <= Date.now()) {
          continue;
        }

        const isSentByMe = message.senderId === myUserId;

        const plainText = await CryptoManager.decryptWithECDH(
          myPrivateKey,
          senderPublicKey,
          message.content,
        );

        fetchedMessageIdsRef.current.add(message.id);

        onReceiveFetchedMessageRef.current?.({
          id: message.id,
          plainText,
          sender: isSentByMe ? "sent" : "received",
          dateCreated: message.dateCreated
            ? String(message.dateCreated)
            : undefined,
          expiresAt: message.dateExpire || undefined,
          status: isSentByMe ? "delivered" : undefined,
        });

        if (
          !isSentByMe &&
          isActiveRef.current &&
          requestToken &&
          message.chatId &&
          message.senderId
        ) {
          try {
            await markChatSeen(requestToken, message.chatId, message.senderId);
          } catch (error) {
            console.error("Seen failed for fetched message", error);
          }
        }
      }
    } catch (error) {
      if (isChatNotFoundError(error)) {
        console.warn("Chat not found. Expiring local session.");

        try {
          await closePrivateChatPresenceConnection();
        } catch (closeError) {
          console.error(
            "Failed to close SignalR after chat not found:",
            closeError,
          );
        }

        await expireSessionNow();
        window.dispatchEvent(new Event("idless:session-updated"));

        navigate("/no-active-chats", { replace: true });
        return;
      }

      console.error("Failed to refresh missed messages:", error);
    }
  }, [requestToken, myUserId, applyTimerFromChatInfo, navigate]);

  useEffect(() => {
    if (!requestToken) return;
    if (!identifier) return;
    if (!myUserId) return;

    const otherSession = loadOtherSession();
    const existingChatId = otherSession?.chatId;

    let isMounted = true;
    const handleIncomingMessage = async (
      encryptedMessage: string,
      otherUserPublicKey?: string | null,
      chatId?: string,
      otherUserId?: string,
    ) => {
      try {
        const myPrivateKey = await CryptoManager.getStoredPrivateKeyBase64();
        const senderPublicKey =
          otherUserPublicKey || sessionStorage.getItem("otherUserPubKey");

        if (!myPrivateKey || !senderPublicKey) {
          onReceiveDecryptionFailureRef.current?.();
          return;
        }

        const plainText = await CryptoManager.decryptWithECDH(
          myPrivateKey,
          senderPublicKey,
          encryptedMessage,
        );

        onReceiveMessageRef.current?.(plainText);

        if (isActiveRef.current && requestToken && chatId && otherUserId) {
          try {
            await markChatSeen(requestToken, chatId, otherUserId);
          } catch (error) {
            console.error("Seen failed", error);
          }
        }
      } catch (error) {
        try {
          if (chatId && otherUserId) {
            await markChatSeen(requestToken, chatId, otherUserId);
          }
        } catch (seenError) {
          console.error("Seen failed", seenError);
        }

        console.error("Decryption error", error);
        onReceiveDecryptionFailureRef.current?.();
      }
    };

    const handleRealtimeEvent = async (payload: PrivateChatPresencePayload) => {
      if (!isMounted) return;

      if (payload.type === "message_created") {
        if (!payload.value) return;

        let message: any;

        try {
          message = JSON.parse(payload.value);
        } catch (error) {
          console.error("Failed to parse realtime message payload:", error);
          return;
        }

        if (message.senderId === myUserId || message.sender?.id === myUserId) {
          return;
        }

        await handleIncomingMessage(
          message.content,
          message.sender?.publicKey,
          message.chatId,
          message.senderId ?? message.sender?.id,
        );
        return;
      }

      if (payload.type === "private_chat_seen") {
        handleSeen({ chatId: payload.chatId });
        return;
      }

      if (payload.type === "updatedChat") {
        applyUpdatedChat(payload);
        return;
      }

      if (payload.type === "chatDeleted") {
        await expireSessionNow().catch(() => {});
        navigate("/no-active-chats", { replace: true });
        return;
      }
    };

    const handleSeen = (payload: SeenPayload) => {
      const currentChatId = loadOtherSession()?.chatId;

      if (payload.chatId && currentChatId && payload.chatId !== currentChatId) {
        return;
      }

      onSeenRef.current?.();
    };

    const connectWithFreshChat = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (!requestToken || !identifier) return;

        let chatCreateData;
        let signalRIdentifier = identifier;

        if (mode === "chatNumber") {
          if (!creatingChatRef.current) {
            creatingChatRef.current = createPrivateChat(
              identifier,
              requestToken,
            );
          }

          chatCreateData = await creatingChatRef.current;
        } else {
          if (!publicId) {
            throw new Error("Missing public id");
          }

          if (!creatingChatRef.current) {
            creatingChatRef.current = createPrivateChatByPublicId(
              publicId,
              requestToken,
            );
          }

          chatCreateData = await creatingChatRef.current;

          const lastingPublicId = chatCreateData.createdPublicId ?? publicId;

          console.log("lasting pub id", lastingPublicId);

          signalRIdentifier = getSignalRIdentifierFromPublicId(lastingPublicId);

          window.history.replaceState(
            null,
            "",
            `/publicid/connect/${encodeURIComponent(lastingPublicId)}`,
          );

          onPublicIdReplaced?.(lastingPublicId);
        }

        saveOtherSession(chatCreateData, signalRIdentifier);

        setChatConnection(chatCreateData.chatId);
        // resolvedDisplayName is always sent and is the authoritative name to show;
        // raw name may be omitted entirely when the counterparty is behind a
        // Context Tag and hasn't disclosed it (see PLAN.md §3 Q9).
        setOtherUserName(chatCreateData.resolvedDisplayName ?? chatCreateData.name ?? "");

        applyTimerFromChatInfo(chatCreateData);

        const chatId = chatCreateData.chatId;

        if (chatId) {
          const parts = chatId.split("_");

          if (parts.length === 3) {
            const additionalId = parts[2];
            sessionStorage.setItem("additionalId", additionalId);
          }
        }

        const createdConnection = await establishPrivateChatPresenceConnection(
          myUserId,
          requestToken,
          handleRealtimeEvent,
        );

        if (!isMounted) {
          return;
        }

        setConnection(createdConnection);
        setIsChatReady(true);
      } catch (error) {
        console.error("Error during setup:", error);

        if (isMounted) {
          setIsChatReady(false);
        }

        const message = getErrorMessage(error);

        alert(message || "Could not initialize chat");

        // ✅ Important: reset the failed promise so future attempts are not stuck
        creatingChatRef.current = null;

        // ✅ Clean any partial session leftovers
        await closePrivateChatPresenceConnection().catch(() => {});
        await expireSessionNow().catch(() => {});

        // ✅ Send user away from NewChatReady
        if (isMounted) {
          navigate("/no-active-chats", { replace: true });
        }
      }
    };

    const reconnectExistingChat = async () => {
      try {
        if (!otherSession || !identifier || !requestToken) return;

        setOtherUserName(otherSession.otherUserName);
        setChatConnection(otherSession.chatId || "");

        if (otherSession.otherUserId) {
          await refreshMissedMessages();
        }

        const createdConnection = await establishPrivateChatPresenceConnection(
          myUserId,
          requestToken,
          handleRealtimeEvent,
        );

        if (!isMounted) {
          return;
        }

        setConnection(createdConnection);
        setIsChatReady(true);
      } catch (error) {
        if (isMounted) {
          setIsChatReady(false);
        }
        console.error("Reconnect failed:", error);
      }
    };

    if (!existingChatId) {
      void connectWithFreshChat();
    } else {
      void reconnectExistingChat();
    }

    return () => {
      isMounted = false;
      removePrivateChatPresenceListener(handleRealtimeEvent);

      console.log("Private chat realtime listener removed");
    };
  }, [
    identifier,
    mode,
    myUserId,
    publicId,
    requestToken,
    applyTimerFromChatInfo,
    applyUpdatedChat,
    navigate,
    refreshMissedMessages,
    onPublicIdReplaced,
  ]);

  return {
    connection,
    isChatReady,
    otherUserName,
    chatConnection,
    refreshMissedMessages,
  };
}
